import React from 'react'
import { router } from '@inertiajs/react'
import { Receipt, Download, FileText } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'

const rand = (n) => `R${(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const titleize = (s) => (s || '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
const PERIODS = [['day', 'Day'], ['month', 'Month'], ['year', 'Year']]

function Kpi({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-brand-border bg-white px-5 py-4">
      <p className="text-xs font-medium uppercase tracking-wide text-brand-muted">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${accent || 'text-brand-ink'}`}>{value}</p>
    </div>
  )
}

// One patient inside a dentist block: account number, every charge code billed (debits) summed to a
// per-patient total, the payment(s) received (credits) below, and a charged-vs-paid reconciliation
// badge so Paul can see at a glance whether what the patient paid matches what they were charged.
function PatientBlock({ pt }) {
  const balance = pt.balance || 0
  let badge
  if (Math.abs(balance) < 0.005) {
    badge = <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">✓ Paid in full — matches</span>
  } else if (balance > 0) {
    badge = <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">Outstanding {rand(balance)}</span>
  } else {
    badge = <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">Credit / overpaid {rand(-balance)}</span>
  }

  // Planned-vs-built charge reconciliation retired from this report (Paul 2026-07-29) — the charted-vs-
  // invoiced charge-check now lives on the Reconciliation page. Only the charged-vs-paid money view remains.
  return (
    <div className="border-t border-brand-border/70">
      {/* patient sub-header: name + account number */}
      <div className="flex flex-wrap items-center justify-between gap-2 bg-brand-surface/30 px-4 py-2">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-brand-ink">{pt.name}</span>
          {pt.account_code && (
            <span className="rounded bg-brand-surface px-1.5 py-0.5 text-xs font-medium text-brand-muted">Account {pt.account_code}</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-brand-muted">Charged <span className="font-semibold text-brand-ink">{rand(pt.charged)}</span></span>
          <span className="text-brand-muted">Paid <span className="font-semibold text-emerald-600">{rand(pt.paid)}</span></span>
          {badge}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-brand-muted">
            <tr>
              <th className="px-3 py-1.5 font-semibold">Date</th>
              <th className="px-3 py-1.5 font-semibold">Tooth</th>
              <th className="px-3 py-1.5 font-semibold">Code</th>
              <th className="px-3 py-1.5 font-semibold">Description</th>
              <th className="px-3 py-1.5 text-center font-semibold">Units</th>
              <th className="px-3 py-1.5 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-border/50">
            {pt.charges.map((l, i) => (
              <tr key={`c${i}`} className="hover:bg-brand-surface/30">
                <td className="whitespace-nowrap px-3 py-1.5 text-brand-muted">{l.date}</td>
                <td className="px-3 py-1.5 text-brand-muted">{l.tooth || '—'}</td>
                <td className="px-3 py-1.5 font-medium text-brand-ink">{l.code}</td>
                <td className="px-3 py-1.5 text-brand-muted">{l.description}</td>
                <td className="px-3 py-1.5 text-center text-brand-muted">{l.units}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right font-medium text-brand-ink">{rand(l.amount)}</td>
              </tr>
            ))}
            {/* per-patient charge subtotal */}
            <tr className="bg-brand-surface/40 font-semibold">
              <td className="px-3 py-1.5 text-brand-ink" colSpan={5}>Total charged</td>
              <td className="whitespace-nowrap px-3 py-1.5 text-right text-brand-ink">{rand(pt.charged)}</td>
            </tr>
            {/* payments received from this patient/account in the period (credits) */}
            {pt.payments.map((p, i) => (
              <tr key={`p${i}`} className="bg-emerald-50/40">
                <td className="whitespace-nowrap px-3 py-1.5 text-brand-muted">{p.date}</td>
                <td className="px-3 py-1.5 text-brand-muted">—</td>
                <td className="px-3 py-1.5 text-xs font-semibold uppercase text-emerald-700">{p.method}</td>
                <td className="px-3 py-1.5 text-brand-muted">Payment received{p.reference ? ` · ${p.reference}` : ''}</td>
                <td className="px-3 py-1.5 text-center text-brand-muted">—</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right font-medium text-emerald-700">{rand(p.amount)}</td>
              </tr>
            ))}
            {pt.payments.length === 0 && (
              <tr>
                <td className="px-3 py-1.5 text-xs italic text-brand-muted" colSpan={6}>No payment received in this period.</td>
              </tr>
            )}
            {/* charged-vs-paid reconciliation row */}
            <tr className="border-t-2 border-brand-border bg-brand-surface/60 font-semibold">
              <td className="px-3 py-1.5 text-brand-ink" colSpan={4}>Paid {rand(pt.paid)} of {rand(pt.charged)} charged</td>
              <td className="px-3 py-1.5 text-right text-brand-muted">Balance</td>
              <td className={`whitespace-nowrap px-3 py-1.5 text-right ${Math.abs(balance) < 0.005 ? 'text-emerald-600' : balance > 0 ? 'text-amber-600' : 'text-sky-600'}`}>{rand(balance)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function TransactionReport({ period = 'day', date = '', label = '', navigation = {}, rows = [], totals_by_method = {}, total = 0, count = 0, practice_total = 0, production_total = 0, turnover_by_provider = [], turnover_total = 0, analysis = [], from = '', to = '', provider = '', providers = [], all_providers = [] }) {
  const reload = (next) =>
    router.get('/reporting/transactions', { period, date, from, to, provider, ...next }, { preserveState: true, preserveScroll: true, replace: true })
  const qs = new URLSearchParams(Object.entries({ period, date, from, to, provider }).filter(([, v]) => v)).toString()

  return (
    <DashboardLayout>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary"><Receipt size={18} className="text-white" /></div>
          <div>
            <h1 className="text-xl font-semibold text-brand-ink">Transaction report</h1>
            <p className="text-sm text-brand-muted">Money received — {label}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href={`/reporting/transactions.csv?${qs}`}
             className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border bg-white px-3 py-2 text-sm font-medium text-brand-ink hover:bg-brand-surface">
            <Download size={15} /> CSV
          </a>
          <a href={`/reporting/transactions.xlsx?${qs}`}
             className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100">
            <Download size={15} /> Excel (.xlsx)
          </a>
          <a href={`/reporting/transactions.pdf?${qs}`} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-2 text-sm font-medium text-white hover:bg-brand-primary-dark">
            <FileText size={15} /> PDF
          </a>
        </div>
      </div>

      {/* Period + date controls + dentist scope */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="inline-flex overflow-hidden rounded-lg border border-brand-border">
          {PERIODS.map(([key, lbl]) => (
            <button key={key} onClick={() => reload({ period: key })}
              className={`px-4 py-2 text-sm font-medium transition-colors ${period === key ? 'bg-brand-primary text-white' : 'bg-white text-brand-ink hover:bg-brand-surface'}`}>
              {lbl}
            </button>
          ))}
        </div>

        {/* §3 Date stepper — one-tap previous / next (steps by the current period unit); hidden on custom */}
        {period !== 'custom' && navigation.prev_date ? (
          <div className="inline-flex items-center gap-1">
            <button type="button" aria-label="Previous" title="Previous" onClick={() => reload({ date: navigation.prev_date })}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-brand-border bg-white text-xl leading-none text-brand-ink hover:bg-brand-surface">‹</button>
            <input type="date" value={date} onChange={(e) => reload({ date: e.target.value })}
              className="rounded-lg border border-brand-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
            <button type="button" aria-label="Next" title="Next" onClick={() => reload({ date: navigation.next_date })}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-brand-border bg-white text-xl leading-none text-brand-ink hover:bg-brand-surface">›</button>
            <button type="button" onClick={() => reload({ date: navigation.today })}
              className="rounded-lg border border-brand-border bg-white px-3 py-2 text-sm text-brand-muted hover:bg-brand-surface">Today</button>
          </div>
        ) : (
          <input type="date" value={date} onChange={(e) => reload({ date: e.target.value })}
            className="rounded-lg border border-brand-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
        )}

        {/* §2.1 Dentist scope — segmented toggle (the real dentists) with an All / Practice default */}
        {providers.length > 0 && (
          <div className="inline-flex overflow-hidden rounded-lg border border-brand-border">
            <button type="button" onClick={() => reload({ provider: '' })}
              className={`px-4 py-2 text-sm font-medium transition-colors ${!provider ? 'bg-brand-primary text-white' : 'bg-white text-brand-ink hover:bg-brand-surface'}`}>All</button>
            {providers.map((p) => {
              const short = p.replace(/^Dr\s+/i, 'Dr ').split(' ').slice(0, 2).join(' ')
              const isActive = (provider || '').toLowerCase() === p.toLowerCase()
              return (
                <button key={p} type="button" onClick={() => reload({ provider: p })}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${isActive ? 'bg-brand-primary text-white' : 'bg-white text-brand-ink hover:bg-brand-surface'}`}>{short}</button>
              )
            })}
          </div>
        )}
      </div>

      {/* Custom date range + dentist filter — for paying locums over their exact engagement dates */}
      <div className="mb-5 flex flex-wrap items-end gap-3 rounded-xl border border-brand-border bg-white p-3">
        <label className="text-xs font-medium text-brand-muted">From
          <input type="date" value={from} onChange={(e) => reload({ from: e.target.value, to: to || e.target.value })}
            className="mt-1 block rounded-lg border border-brand-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
        </label>
        <label className="text-xs font-medium text-brand-muted">To
          <input type="date" value={to} onChange={(e) => reload({ to: e.target.value, from: from || e.target.value })}
            className="mt-1 block rounded-lg border border-brand-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
        </label>
        <label className="text-xs font-medium text-brand-muted">Dentist / locum
          <select value={provider} onChange={(e) => reload({ provider: e.target.value })}
            className="mt-1 block rounded-lg border border-brand-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30">
            <option value="">All dentists</option>
            {all_providers.map((p) => <option key={p} value={p}>{titleize(p)}</option>)}
          </select>
        </label>
        {(from || to || provider) && (
          <button onClick={() => reload({ from: '', to: '', provider: '', period: 'month' })}
            className="rounded-lg border border-brand-border bg-white px-3 py-2 text-sm text-brand-muted hover:bg-brand-surface">Clear</button>
        )}
      </div>

      {/* Money flow — practice-wide by default; scoped to the selected dentist when one is picked (§2.2) */}
      {provider && (
        <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-brand-surface/40 px-3 py-2 text-xs text-brand-muted">
          <span className="font-semibold text-brand-ink">Money flow · {titleize(provider)}</span>
          <span>Billed by dentist <span className="font-semibold text-brand-ink">{rand(production_total)}</span></span>
          <span>Practice-wide received (all dentists) <span className="font-semibold text-brand-ink">{rand(practice_total)}</span></span>
          <span className="italic">Only invoice-linked payments attribute to a dentist; account-level payments stay in the All view.</span>
        </div>
      )}
      {/* Totals by method */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {provider && <Kpi label={`Billed by ${titleize(provider)}`} value={rand(production_total)} accent="text-brand-primary" />}
        <Kpi label="Card" value={rand(totals_by_method.card)} />
        <Kpi label="Cash" value={rand(totals_by_method.cash)} />
        <Kpi label="EFT" value={rand(totals_by_method.eft)} />
        <Kpi label="Credit applied" value={rand(totals_by_method.credit)} />
        {/* Discount = practice write-down, NOT money received — shown muted, excluded from Total received */}
        <Kpi label="Discount" value={rand(totals_by_method.discount)} accent="text-amber-600" />
        <Kpi label={provider ? 'Received (attributable)' : 'Total received'} value={rand(total)} accent="text-emerald-600" />
      </div>

      {/* Turnover by dentist — every charge code billed (the Elixir Transaction Analysis) */}
      {turnover_by_provider.length > 0 && (
        <div className="mt-8">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-brand-muted">Turnover by dentist — charge codes billed ({label})</p>
            <p className="text-sm font-semibold text-brand-ink">Total turnover {rand(turnover_total)}</p>
          </div>

          {/* per-dentist totals */}
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {turnover_by_provider.map((p) => (
              <div key={p.provider} className="rounded-xl border border-brand-border bg-white px-5 py-4">
                <p className="truncate text-xs font-medium uppercase tracking-wide text-brand-muted">{titleize(p.provider)}</p>
                <p className="mt-1 text-2xl font-semibold text-brand-ink">{rand(p.total)}</p>
                <p className="text-[11px] text-brand-muted">{p.count} charge-code line(s)</p>
              </div>
            ))}
          </div>

          {/* per-dentist detail — grouped per patient: account number, summed charges, and the
              payment(s) received below each patient with a charged-vs-paid reconciliation. */}
          {analysis.map((prov) => (
            <div key={prov.provider} className="mb-5 overflow-hidden rounded-xl border border-brand-border bg-white">
              <div className="flex items-center justify-between border-b border-brand-border bg-brand-surface/60 px-4 py-2">
                <span className="text-sm font-semibold text-brand-ink">{titleize(prov.provider)}</span>
                <span className="text-sm font-semibold text-brand-ink">
                  Charged {rand(prov.charged)} · <span className="text-emerald-600">Paid {rand(prov.paid)}</span>
                </span>
              </div>

              {prov.patients.map((pt, pi) => (
                <PatientBlock key={pi} pt={pt} />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Payments received to accounts */}
      <p className="mt-8 mb-2 text-[10px] font-semibold uppercase tracking-wide text-brand-muted">Payments received — to accounts</p>
      <div className="mt-6 overflow-hidden rounded-xl border border-brand-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-brand-surface/60 text-left text-xs uppercase tracking-wide text-brand-muted">
            <tr>
              <th className="px-4 py-2.5 font-semibold">Date / time</th>
              <th className="px-4 py-2.5 font-semibold">Patient / account</th>
              <th className="px-4 py-2.5 font-semibold">Method</th>
              <th className="px-4 py-2.5 font-semibold">Type</th>
              <th className="px-4 py-2.5 font-semibold">Reference</th>
              <th className="px-4 py-2.5 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-border/60">
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-brand-muted">No transactions in this period.</td></tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i} className="hover:bg-brand-surface/30">
                  <td className="whitespace-nowrap px-4 py-2 text-brand-muted">{r.time}</td>
                  <td className="px-4 py-2 font-medium text-brand-ink">{r.party}</td>
                  <td className="px-4 py-2"><span className="rounded-full bg-brand-surface px-2 py-0.5 text-xs capitalize">{r.method}</span></td>
                  <td className="px-4 py-2 capitalize text-brand-muted">{(r.kind || '').replace('_', ' ')}</td>
                  <td className="px-4 py-2 text-brand-muted">{r.reference || '—'}</td>
                  <td className={`whitespace-nowrap px-4 py-2 text-right font-semibold ${r.amount < 0 ? 'text-brand-danger' : 'text-brand-ink'}`}>{rand(r.amount)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-brand-muted">{count} transaction(s) — {label}</p>
    </DashboardLayout>
  )
}
